import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ICONS } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const StartupTechConsultingDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'mvp-development',
      title: 'MVP Development',
      description: 'Launch your product faster with a focused Minimum Viable Product. We help you validate your idea in the market with a lean, functional product that attracts early users and investors.',
      icon: ICONS.mvp,
      features: [
        'Product Discovery & Validation',
        'Lean Feature Prioritization',
        'Rapid Prototyping',
        'User Testing & Feedback Loops',
        'Investor-Ready Demos',
        'Scalable Architecture Foundation'
      ]
    },
    {
      id: 'tech-stack-consulting',
      title: 'Tech Stack Consulting',
      description: 'Choose the right technologies for your startup. We help you select a tech stack that balances development speed, scalability, cost, and talent availability in the Dubai market.',
      icon: ICONS.techStack,
      features: [
        'Technology Assessment & Selection',
        'Build vs Buy Analysis',
        'Vendor Evaluation',
        'Architecture Design Review',
        'Scalability Planning',
        'Cost Optimization Strategy'
      ]
    },
    {
      id: 'fractional-cto',
      title: 'Fractional CTO Services',
      description: 'Get executive-level technical leadership without the full-time commitment. Our fractional CTO services provide strategic guidance, team building, and technical oversight for growing startups.',
      icon: ICONS.person,
      features: [
        'Technical Strategy & Roadmap',
        'Engineering Team Building',
        'Investor Technical Due Diligence',
        'Technical Debt Management',
        'Process & Methodology Optimization',
        'Board & Stakeholder Communication'
      ]
    },
    {
      id: 'scale-engineering',
      title: 'Scale-Up Engineering',
      description: 'Ready to scale? We help startups transition from MVP to production-grade systems that can handle growth. From infrastructure to team processes, we prepare you for the next stage.',
      icon: ICONS.growth,
      features: [
        'Performance Optimization',
        'Infrastructure Scaling',
        'CI/CD Pipeline Implementation',
        'Monitoring & Observability',
        'Security Hardening',
        'Engineering Team Augmentation'
      ]
    }
  ]

  const startupStages = [
    {
      stage: 'Pre-Seed / Idea Stage',
      services: ['Product Discovery', 'MVP Planning', 'Tech Stack Selection', 'Prototype Development'],
      Icon: ICONS.ideaStage
    },
    {
      stage: 'Seed / Early Stage',
      services: ['MVP Development', 'User Testing', 'Investor Demos', 'Initial Team Building'],
      Icon: ICONS.seedStage
    },
    {
      stage: 'Series A / Growth',
      services: ['Scale-Up Engineering', 'Team Expansion', 'Process Optimization', 'Security Audit'],
      Icon: ICONS.launch
    },
    {
      stage: 'Series B+ / Scale',
      services: ['Enterprise Architecture', 'Platform Engineering', 'International Expansion', 'M&A Technical DD'],
      Icon: ICONS.enterprise
    }
  ]

  const ecosystemPartners = [
    { name: 'DIFC Innovation Hub', type: 'Accelerator' },
    { name: 'Hub71 Abu Dhabi', type: 'Accelerator' },
    { name: 'Dubai Future Foundation', type: 'Government' },
    { name: 'Startupbootcamp', type: 'Accelerator' },
    { name: 'Flat6Labs', type: 'Accelerator' },
    { name: 'In5', type: 'Incubator' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const faqs = [
    {
      question: 'How much does it cost to build an MVP in Dubai?',
      answer: 'MVP development costs in Dubai typically range from AED 35,000 to AED 150,000 depending on complexity, features, and technology stack. A simple web-based MVP with core features can be built for AED 35,000 to AED 60,000, while a more complex platform with mobile apps, real-time features, and third-party integrations ranges from AED 80,000 to AED 150,000. We provide detailed proposals with fixed milestones so you know the exact cost before starting.'
    },
    {
      question: 'How long does it take to build an MVP?',
      answer: 'A typical MVP for a Dubai startup takes 4 to 8 weeks from kickoff to launch. Simple MVPs with basic functionality can be delivered in 3 to 4 weeks, while more complex products with multiple user roles, payment integration, and API connections take 6 to 8 weeks. We use lean development practices and weekly sprints so you can track progress and provide feedback throughout the process.'
    },
    {
      question: 'What does a fractional CTO do for a startup?',
      answer: 'A fractional CTO provides part-time executive-level technical leadership for your startup. This includes defining technology strategy and roadmaps, selecting the right tech stack, building and managing engineering teams, preparing for investor technical due diligence, managing technical debt, and communicating with stakeholders and board members. It gives you CTO-level expertise at a fraction of the cost of a full-time hire, typically for AED 8,000 to AED 25,000 per month depending on hours and scope.'
    },
    {
      question: 'Which tech stack do you recommend for Dubai startups?',
      answer: 'The best tech stack depends on your product type, team, and growth plans. For web applications, we commonly recommend React or Next.js with Node.js and PostgreSQL. For mobile apps, React Native or Flutter provides cross-platform efficiency. For high-scale platforms, we use microservices with Kubernetes on AWS or GCP. We consider factors like developer availability in the Dubai market, scalability requirements, and time-to-market when making recommendations.'
    },
    {
      question: 'Do you work with startups in DIFC, Hub71, and other UAE accelerators?',
      answer: 'Yes, we work extensively with startups across UAE accelerators and free zones including DIFC Innovation Hub, Hub71 Abu Dhabi, In5, Startupbootcamp, Flat6Labs, and Dubai Future Foundation programs. We understand the specific requirements and timelines of accelerator programs and can adapt our services to match your program milestones and demo day deadlines.'
    },
    {
      question: 'Can you help prepare for investor fundraising?',
      answer: 'Yes, we help startups prepare for fundraising at every stage from pre-seed to Series B and beyond. This includes building investor-ready product demos, creating technical documentation and architecture diagrams for due diligence, optimizing product metrics and analytics dashboards, and providing fractional CTO support during investor conversations. We have helped our clients raise over $20M in combined funding.'
    },
    {
      question: 'What is the difference between an MVP and a prototype?',
      answer: 'A prototype is a visual demonstration of your product concept used to validate the idea with stakeholders and early users. It may not have real functionality behind the screens. An MVP (Minimum Viable Product) is a working product with core features that real customers can use. It includes functional backend systems, data storage, and user authentication. We typically recommend starting with wireframes, then a clickable prototype for validation, and finally an MVP for market launch.'
    }
  ]

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Startup Tech Consulting Dubai', path: '/startup-tech-consulting-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Startup Tech Consulting Dubai | MVP Development & Fractional CTO UAE"
        description="Tech consulting for Dubai startups. MVP development, fractional CTO services, and scale-up engineering. We help founders build, launch, and scale their technology products."
        keywords={[
          'startup tech consulting dubai',
          'mvp development dubai',
          'fractional cto dubai',
          'startup consulting uae',
          'tech advisor dubai',
          'cto as a service dubai',
          'startup engineering dubai',
          'product development uae',
          'tech startup dubai',
          'startup accelerator consulting',
          'difc startup services',
          'hub71 tech consulting'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              For Founders & Startups
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Startup Tech Consulting in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              From idea to IPO, we partner with Dubai startups to build exceptional technology products.
              MVP development, fractional CTO services, and scale-up engineering for founders who
              want to move fast without breaking things.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Book Founder Call
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { stat: '50+', label: 'Startups Launched' },
              { stat: '$20M+', label: 'Client Funding Raised' },
              { stat: '4 weeks', label: 'Average MVP Timeline' },
              { stat: '90%', label: 'Client Retention Rate' }
            ].map((item, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{item.stat}</div>
                <div className="text-primary-100">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                How We Help Startups
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Comprehensive tech services designed for the unique challenges and pace of startups.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => {
                const Icon = service.icon
                return (
                  <motion.div
                    key={service.id}
                    variants={itemVariants}
                    className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <Icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Startup Stages Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Services by Startup Stage
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We tailor our approach to your current stage and growth goals.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {startupStages.map((stage, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-600"
                >
                  <div className="w-12 h-12 mb-4" aria-hidden="true">
                    <stage.Icon className="w-full h-full" darkTreatment="none" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {stage.stage}
                  </h3>
                  <ul className="space-y-2">
                    {stage.services.map((service, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                        <svg className="w-4 h-4 text-primary-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {service}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Dubai Startup Ecosystem
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We work with startups across Dubai's vibrant ecosystem, from accelerators to free zones.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {ecosystemPartners.map((partner, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-gray-50 dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-600"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{partner.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{partner.type}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about startup tech consulting in Dubai answered by our team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                All Dubai Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Build Your Startup?"
        description="Let's discuss how we can help you turn your vision into a successful technology product. Book a free founder call to explore your options."
        primary={{ kind: 'calendly', label: 'Schedule Free Founder Call' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default StartupTechConsultingDubai
